import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/store/hooks'
import { verifyPayment, fetchCurrentSubscription } from '@/store/slices/subscriptionSlice'
import { getCurrentUser } from '@/store/slices/authSlice'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

export const PaymentCallbackPage: React.FC = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending')
    const [statusMessage, setStatusMessage] = useState('')

    useEffect(() => {
        const verifyAndRedirect = async () => {
            const reference = searchParams.get('reference')
            const status = searchParams.get('status')

            // If payment was cancelled by user
            if (status === 'cancelled' || !reference) {
                setVerificationStatus('error')
                setStatusMessage('Payment was cancelled. Redirecting to subscription page...')
                setTimeout(() => navigate('/subscription'), 3000)
                return
            }

            try {
                // Verify payment with the reference
                await dispatch(verifyPayment(reference)).unwrap()

                setVerificationStatus('success')
                setStatusMessage('Payment verified successfully! Your subscription has been updated.')

                // Refresh subscription data and user profile (to get updated limits)
                await dispatch(fetchCurrentSubscription())
                await dispatch(getCurrentUser())

                // Redirect to dashboard or subscription page after 3 seconds
                setTimeout(() => {
                    navigate('/dashboard')
                }, 3000)
            } catch (err: any) {
                setVerificationStatus('error')
                setStatusMessage(
                    err || 'Failed to verify payment. Please check your subscription page for status.'
                )

                // Try to refresh subscription and user data anyway in case it was processed
                await dispatch(fetchCurrentSubscription())
                await dispatch(getCurrentUser())

                // Redirect after 5 seconds
                setTimeout(() => navigate('/subscription'), 5000)
            }
        }

        verifyAndRedirect()
    }, [searchParams, dispatch, navigate])

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-8">
                {verificationStatus === 'pending' ? (
                    <>
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                            <div className="animate-spin">
                                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            </div>
                        </div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            Verifying Payment
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Please wait while we verify your payment...
                        </p>
                    </>
                ) : verificationStatus === 'success' ? (
                    <>
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                            <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            Payment Successful!
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {statusMessage}
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                        >
                            Go to Dashboard
                        </button>
                    </>
                ) : (
                    <>
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
                        </div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            Payment Verification Failed
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {statusMessage}
                        </p>
                        <button
                            onClick={() => navigate('/subscription')}
                            className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                        >
                            Back to Subscription
                        </button>
                    </>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-6">
                    You will be automatically redirected shortly.
                </p>
            </div>
        </div>
    )
}